/**
 * DDX-Plus Firestore access (client). Collections prefixed with `ddx`.
 */

import firebase from 'firebase/compat/app';
import { db, functions } from './firebase';
import {
  getCantMissRegistry,
  getAllCantMissRegistryIds,
  CANT_MISS_REGISTRY_VERSION,
  chiefComplaintNeedsHydration,
  writeFeedbackEvent,
} from '@healthdesk/shared-utils';

/** @param {unknown} err */
export function isFirestorePermissionDenied(err) {
  if (!err || typeof err !== 'object') return false;
  const c = /** @type {{ code?: string; message?: string }} */ (err).code;
  if (c === 'permission-denied' || c === 'firestore/permission-denied') return true;
  const msg = String(/** @type {{ message?: string }} */ (err).message || '');
  return msg.includes('Missing or insufficient permissions');
}

export const DDX_COLLECTIONS = {
  CHIEF_COMPLAINTS: 'ddxChiefComplaints',
  /** KB projection + getFullDdx: diagnosis-shaped KB documents (post-merge live on `ddxConditions`). */
  DIAGNOSES: 'ddxConditions',
  FEATURES: 'ddxFeatures',
  EDGES: 'ddxFeatureDiagnosisEdges',
  CANT_MISS_REGISTRY: 'ddxCantMissRegistry',
  CALCULATORS: 'ddxCalculators',
  FEEDBACK_EVENTS: 'ddxFeedbackEvents',
  AUTHORITATIVE_SOURCES: 'ddxAuthoritativeSources',
  KB_VERIFICATION_QUEUE: 'ddxKbVerificationQueue',
  DDX_AUDIT_JOBS: 'ddxDdxAuditJobs',
  BENCHMARK_RUNS: 'ddxBenchmarkRuns',
  BENCHMARK_VIGNETTES: 'ddxBenchmarkVignettes',
  BENCHMARK_BASELINES: 'ddxBenchmarkBaselines',
  COMPARISON_RUNS: 'ddxComparisonRuns',
  BATCH_SEED_RUNS: 'ddxBatchSeedRuns',
  BENCHMARK_DIGESTS: 'ddxBenchmarkDigests',
  CONTEXT_PROFILE_PRESETS: 'ddxContextProfilePresets',
  SHIFT_FACTORS: 'ddxShiftFactors',
  CONDITIONS: 'ddxConditions',
  SHIFT_EDGES: 'ddxShiftEdges',
  FACTOR_QUEUE: 'ddxFactorQueue',
};

let _ddxCalculatorsCache = { at: 0, list: /** @type {object[]} */ ([]) };
const DDX_CALC_CACHE_MS = 60_000;
let _knownShiftFactorsCache = { at: 0, ids: /** @type {string[]} */ ([]) };
const SHIFT_FACTORS_CACHE_MS = 10 * 60_000;

/**
 * Phase 3 — load calculator pathway definitions from Firestore (short TTL cache).
 * @returns {Promise<object[]>}
 */
export async function fetchDdxCalculatorsCatalog() {
  const now = Date.now();
  if (_ddxCalculatorsCache.list.length > 0 && now - _ddxCalculatorsCache.at < DDX_CAL_CACHE_MS) {
    return _ddxCalculatorsCache.list;
  }
  try {
    const snap = await db.collection(DDX_COLLECTIONS.CALCULATORS).get();
    const list = [];
    snap.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() });
    });
    _ddxCalculatorsCache = { at: now, list };
    return list;
  } catch (e) {
    console.warn('[ddxService] fetchDdxCalculatorsCatalog failed', e);
    return [];
  }
}

export function clearDdxCalculatorsCatalogCache() {
  _ddxCalculatorsCache = { at: 0, list: [] };
}

export function clearKnownShiftFactorsCache() {
  _knownShiftFactorsCache = { at: 0, ids: [] };
}

/**
 * Phase 1 KB seed (Cloud Function). Requires Global LLM Manager claim.
 * @param {{ dryRun?: boolean }} [options]
 * @returns {Promise<object>}
 */
export async function seedDdxPhase1Kb(options = {}) {
  const fn = functions.httpsCallable('seedDdxPhase1Kb');
  const res = await fn({ dryRun: Boolean(options.dryRun) });
  return res.data;
}

/**
 * Phase 2 authoritative sources seed. Requires Global LLM Manager claim.
 * @param {{ dryRun?: boolean }} [options]
 * @returns {Promise<object>}
 */
export async function seedDdxAuthoritativeSources(options = {}) {
  const fn = functions.httpsCallable('seedDdxAuthoritativeSources');
  const res = await fn({ dryRun: Boolean(options.dryRun) });
  return res.data;
}

/**
 * Phase 3 Feature K — seed `ddxCalculators` (Global LLM Manager).
 * @param {{ dryRun?: boolean }} [options]
 */
export async function seedDdxCalculators(options = {}) {
  const fn = functions.httpsCallable('seedDdxCalculators');
  const res = await fn({ dryRun: Boolean(options.dryRun) });
  return res.data;
}

/**
 * Phase 3 Feature M — append snapshot + update session state.
 * @param {object} payload
 */
export async function processDdxSessionTransition(payload) {
  const fn = functions.httpsCallable('processDdxSessionTransition');
  const res = await fn(payload);
  return res.data;
}

/**
 * Phase 2 gap-fill: server-side search + ddx-source-extraction.
 * @param {object} payload - chiefComplaintId, gapTypes, extraction, ccLabel?
 */
export async function callDdxAuthoritativeSourceConsult(payload) {
  const fn = functions.httpsCallable('ddxAuthoritativeSourceConsult');
  const res = await fn(payload);
  return res.data;
}

/**
 * Phase 2 verification queue writes (validated server-side).
 * @param {{ items: object[] }} payload
 */
export async function callDdxKbVerificationEnqueue(payload) {
  const fn = functions.httpsCallable('ddxKbVerificationEnqueue');
  const res = await fn(payload);
  return res.data;
}

/**
 * Phase 2 — promote a verification queue diagnosis into `ddxConditions` (Admin batch).
 * @param {{ queueId: string, action: 'promote_diagnosis_tier2' | 'promote_diagnosis_tier1' }} payload
 */
export async function callDdxKbVerificationApplyReview(payload) {
  const fn = functions.httpsCallable('ddxKbVerificationApplyReview');
  const res = await fn(payload);
  return res.data;
}

/**
 * Phase 2 audit enqueue (Firestore onCreate worker).
 * @param {object} payload - sessionId, chiefComplaintId, auditTriggers?, kbSummary?, sourceSummary?, sourceName?, sourceUrl?
 */
export async function writeDdxAuditJob(payload) {
  try {
    await db.collection(DDX_COLLECTIONS.DDX_AUDIT_JOBS).add({
      sessionId: String(payload.sessionId || ''),
      chiefComplaintId: String(payload.chiefComplaintId || ''),
      auditTriggers: Array.isArray(payload.auditTriggers) ? payload.auditTriggers : ['spot_check'],
      kbSummary: typeof payload.kbSummary === 'string' ? payload.kbSummary.slice(0, 12000) : '',
      sourceSummary: typeof payload.sourceSummary === 'string' ? payload.sourceSummary.slice(0, 12000) : '',
      sourceName: payload.sourceName || '',
      sourceUrl: payload.sourceUrl || '',
      enqueuedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  } catch (e) {
    console.warn('[DDx audit] enqueue failed (non-blocking)', e);
  }
}

/**
 * @param {string} chiefComplaintId
 * @returns {Promise<{ id: string, data: Object } | null>}
 */
export async function getChiefComplaintDocument(chiefComplaintId) {
  if (!chiefComplaintId) return null;
  const snap = await db.collection(DDX_COLLECTIONS.CHIEF_COMPLAINTS).doc(chiefComplaintId).get();
  if (!snap.exists) return null;
  return { id: snap.id, data: snap.data() || {} };
}

/**
 * @param {object} ccRecord - Chief complaint fields including relatedEdgeIds
 * @returns {Promise<object[]>}
 */
export async function fetchEdgesForChiefComplaintRecord(ccRecord) {
  const ids = Array.isArray(ccRecord?.relatedEdgeIds) ? ccRecord.relatedEdgeIds : [];
  const out = [];
  for (const id of ids) {
    const s = await db.collection(DDX_COLLECTIONS.EDGES).doc(id).get();
    if (s.exists) out.push({ id: s.id, ...s.data() });
  }
  return out;
}

/**
 * Firestore accessors for {@link import('@healthdesk/shared-utils').projectKnowledgeBundle}.
 * @returns {import('@healthdesk/shared-utils').KbProjectionDeps}
 */
export function createDdxKbProjectionDeps() {
  return {
    getChiefComplaint: async (id) => {
      const doc = await getChiefComplaintDocument(id);
      if (!doc) return null;
      let data = doc.data;
      if (chiefComplaintNeedsHydration(data)) {
        try {
          const fn = functions.httpsCallable('getHydratedDdxChiefComplaint');
          const res = await fn({ chiefComplaintId: id });
          const hydrated = res?.data?.data;
          if (hydrated && typeof hydrated === 'object') {
            const { id: _drop, ...rest } = hydrated;
            data = { ...data, ...rest };
          }
        } catch (e) {
          console.warn('[ddxService] KB projection hydrate failed', e);
        }
      }
      return { id: doc.id, ...data };
    },
    getDiagnosis: async (did) => {
      const s = await db.collection(DDX_COLLECTIONS.DIAGNOSES).doc(did).get();
      return s.exists ? { id: s.id, ...s.data() } : null;
    },
    getFeature: async (fid) => {
      const s = await db.collection(DDX_COLLECTIONS.FEATURES).doc(fid).get();
      return s.exists ? { id: s.id, ...s.data() } : null;
    },
    getEdgesForChiefComplaint: (ccRecord) => fetchEdgesForChiefComplaintRecord(ccRecord),
  };
}

/**
 * Non-blocking clinician feedback for DDX-Plus curation. Requires auth; userId must match caller (rules).
 * @param {object} event - Fields except createdAt
 * @param {string} uid - Firebase auth uid
 */
export async function writeDdxFeedbackEvent(event, uid) {
  if (!uid) return;
  await writeFeedbackEvent(
    {
      db,
      serverTimestamp: () => firebase.firestore.FieldValue.serverTimestamp(),
    },
    { ...event, userId: uid }
  );
}

/**
 * Aggregated clinician feedback for DDX-Plus admin (requires `canWriteDdxPlus` read on rules).
 * @param {number} [days=30]
 * @returns {Promise<{
 *   total: number,
 *   byEventType: Record<string, number>,
 *   cantMissActionCount: number,
 *   withBundleHashCount: number,
 *   cantMissRejectionCount: number,
 *   chiefComplaintsWithMultipleBundles: number,
 *   error?: string
 * }>}
 */
export async function fetchDdxFeedbackSummary(days = 30) {
  const empty = {
    total: 0,
    byEventType: {},
    cantMissActionCount: 0,
    withBundleHashCount: 0,
    cantMissRejectionCount: 0,
    chiefComplaintsWithMultipleBundles: 0,
  };
  try {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const ts = firebase.firestore.Timestamp.fromDate(since);
    const snap = await db
      .collection(DDX_COLLECTIONS.FEEDBACK_EVENTS)
      .where('createdAt', '>=', ts)
      .orderBy('createdAt', 'desc')
      .limit(2500)
      .get();

    const byEventType = {};
    let cantMissActionCount = 0;
    let withBundleHashCount = 0;
    let cantMissRejectionCount = 0;
    /** @type {Record<string, Set<string>>} */
    const bundlesByCc = {};

    snap.forEach((doc) => {
      const d = doc.data();
      const et = typeof d.eventType === 'string' ? d.eventType : 'unknown';
      byEventType[et] = (byEventType[et] || 0) + 1;
      if (et === 'cant_miss_actioned') cantMissActionCount += 1;
      if (d.bundleHash) {
        withBundleHashCount += 1;
        const cc = d.chiefComplaintId;
        const bh = d.bundleHash;
        if (cc && bh) {
          if (!bundlesByCc[cc]) bundlesByCc[cc] = new Set();
          bundlesByCc[cc].add(String(bh));
        }
      }
      if (et === 'dx_rejected' && d.wasInCantMiss) cantMissRejectionCount += 1;
    });

    const chiefComplaintsWithMultipleBundles = Object.values(bundlesByCc).filter(
      (s) => s.size > 1
    ).length;

    return {
      total: snap.size,
      byEventType,
      cantMissActionCount,
      withBundleHashCount,
      cantMissRejectionCount,
      chiefComplaintsWithMultipleBundles,
    };
  } catch (err) {
    console.warn('[ddxService] fetchDdxFeedbackSummary failed', err);
    return {
      ...empty,
      error: err?.message || String(err),
    };
  }
}

/** @typedef {'firestore' | 'no_chief_document' | 'permission_denied'} DdxKbLoadStatus */

/**
 * Hydrate KB slice for a chief complaint. Falls back to in-code can’t-miss registry when Firestore is empty.
 *
 * @param {string} chiefComplaintId
 * @returns {Promise<{
 *   chiefComplaint: Object | null,
 *   diagnoses: Object[],
 *   features: Object[],
 *   edges: Object[],
 *   cantMiss: Object | null,
 *   ddxKbLoadStatus: DdxKbLoadStatus
 * }>}
 */
export async function getFullDdxForComplaint(chiefComplaintId) {
  const cantMiss = getCantMissRegistry(chiefComplaintId);
  /** @param {DdxKbLoadStatus} status */
  const offlineBundle = (status) => ({
    chiefComplaint: null,
    diagnoses: [],
    features: [],
    edges: [],
    cantMiss,
    ddxKbLoadStatus: status,
  });

  try {
    const ccDoc = await getChiefComplaintDocument(chiefComplaintId);
    if (!ccDoc) {
      return offlineBundle('no_chief_document');
    }

    let data = ccDoc.data;
    if (chiefComplaintNeedsHydration(data)) {
      try {
        const fn = functions.httpsCallable('getHydratedDdxChiefComplaint');
        const res = await fn({ chiefComplaintId });
        const hydrated = res?.data?.data;
        if (hydrated && typeof hydrated === 'object') {
          const { id: _drop, ...rest } = hydrated;
          data = { ...data, ...rest };
        }
      } catch (hydrateErr) {
        console.warn('[ddxService] getHydratedDdxChiefComplaint failed', hydrateErr);
      }
    }

    const diagnosisIds = Array.isArray(data.relatedDiagnosisIds) ? data.relatedDiagnosisIds : [];
    const featureIds = Array.isArray(data.relatedFeatureIds) ? data.relatedFeatureIds : [];
    const edgeIds = Array.isArray(data.relatedEdgeIds) ? data.relatedEdgeIds : [];

    const [diagnoses, features, edges] = await Promise.all([
      Promise.all(
        diagnosisIds.map(async (id) => {
          const s = await db.collection(DDX_COLLECTIONS.DIAGNOSES).doc(id).get();
          return s.exists ? { id: s.id, ...s.data() } : null;
        })
      ),
      Promise.all(
        featureIds.map(async (id) => {
          const s = await db.collection(DDX_COLLECTIONS.FEATURES).doc(id).get();
          return s.exists ? { id: s.id, ...s.data() } : null;
        })
      ),
      Promise.all(
        edgeIds.map(async (id) => {
          const s = await db.collection(DDX_COLLECTIONS.EDGES).doc(id).get();
          return s.exists ? { id: s.id, ...s.data() } : null;
        })
      ),
    ]);

    return {
      chiefComplaint: { id: ccDoc.id, ...data },
      diagnoses: diagnoses.filter(Boolean),
      features: features.filter(Boolean),
      edges: edges.filter(Boolean),
      cantMiss,
      ddxKbLoadStatus: 'firestore',
    };
  } catch (e) {
    if (isFirestorePermissionDenied(e)) {
      return offlineBundle('permission_denied');
    }
    throw e;
  }
}

/**
 * Seed `ddxCantMissRegistry` from bundled registry when missing or older version.
 * @returns {Promise<{ written: number, skipped: number }>}
 */
export async function seedCantMissRegistryIfNeeded() {
  const col = db.collection(DDX_COLLECTIONS.CANT_MISS_REGISTRY);
  let written = 0;
  let skipped = 0;
  const ids = getAllCantMissRegistryIds();

  try {
    for (const id of ids) {
      const ref = col.doc(id);
      const snap = await ref.get();
      const local = getCantMissRegistry(id);
      if (!local) continue;
      const remoteVersion = snap.exists ? snap.data()?.seededFromVersion : 0;
      if (snap.exists && remoteVersion >= CANT_MISS_REGISTRY_VERSION) {
        skipped += 1;
        continue;
      }
      await ref.set(
        {
          ...local,
          seededFromVersion: CANT_MISS_REGISTRY_VERSION,
          seededAt: new Date().toISOString(),
        },
        { merge: true }
      );
      written += 1;
    }
  } catch (e) {
    if (isFirestorePermissionDenied(e)) {
      throw new Error(
        'Firestore denied access to ddxCantMissRegistry. Deploy the latest firebase/firestore.rules (ddx* rules) to this project, and ensure users/{yourUid} has role llm-manager-global, chartmind-admin, or dscr-admin for writes.'
      );
    }
    throw e;
  }

  return { written, skipped };
}

/**
 * Recent DDX benchmark runs (Phase 4 quality dashboard). Requires canWriteDdxPlus read.
 * @param {number} [limit=60]
 * @returns {Promise<{ runs: object[], error?: string }>}
 */
export async function fetchDdxBenchmarkRuns(limit = 60) {
  try {
    const snap = await db
      .collection(DDX_COLLECTIONS.BENCHMARK_RUNS)
      .orderBy('timestamp', 'desc')
      .limit(Math.min(Math.max(1, limit), 200))
      .get();
    const runs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { runs };
  } catch (e) {
    if (isFirestorePermissionDenied(e)) {
      return { runs: [], error: 'permission_denied' };
    }
    throw e;
  }
}

/**
 * Persist benchmark summary (Global LLM Manager callable).
 * @param {object} payload — runId, metrics, hasBlockingRegressions, aggregates?, warnings?, triggeredBy?, contextProfileId?
 * @returns {Promise<object>}
 */
export async function recordDdxBenchmarkRunCallable(payload) {
  const fn = functions.httpsCallable('recordDdxBenchmarkRun');
  const res = await fn(payload);
  return res.data;
}

export async function fetchDdxBenchmarkVignettes(limit = 300) {
  const snap = await db
    .collection(DDX_COLLECTIONS.BENCHMARK_VIGNETTES)
    .orderBy('updatedAt', 'desc')
    .limit(Math.min(Math.max(1, limit), 500))
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function upsertDdxBenchmarkVignette(vignette) {
  const id = String(vignette?.id || '').trim();
  if (!id) throw new Error('vignette.id is required');
  await db
    .collection(DDX_COLLECTIONS.BENCHMARK_VIGNETTES)
    .doc(id)
    .set(
      {
        ...vignette,
        id,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdAt: vignette?.createdAt || firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true }
    );
  return { ok: true, id };
}

export async function deleteDdxBenchmarkVignette(id) {
  await db.collection(DDX_COLLECTIONS.BENCHMARK_VIGNETTES).doc(id).delete();
  return { ok: true, id };
}

export async function fetchDdxBaselines(limit = 60) {
  const snap = await db
    .collection(DDX_COLLECTIONS.BENCHMARK_BASELINES)
    .orderBy('createdAt', 'desc')
    .limit(Math.min(Math.max(1, limit), 200))
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchDdxComparisonRuns(limit = 60) {
  const snap = await db
    .collection(DDX_COLLECTIONS.COMPARISON_RUNS)
    .orderBy('updatedAt', 'desc')
    .limit(Math.min(Math.max(1, limit), 200))
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchDdxBatchSeedRuns(limit = 60) {
  const snap = await db
    .collection(DDX_COLLECTIONS.BATCH_SEED_RUNS)
    .orderBy('updatedAt', 'desc')
    .limit(Math.min(Math.max(1, limit), 200))
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchDdxBenchmarkDigests(limit = 30) {
  const snap = await db
    .collection(DDX_COLLECTIONS.BENCHMARK_DIGESTS)
    .orderBy('generatedAt', 'desc')
    .limit(Math.min(Math.max(1, limit), 100))
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function callImportDiskVignettesToFirestore(payload = {}) {
  const fn = functions.httpsCallable('callImportDiskVignettesToFirestore');
  const res = await fn(payload);
  return res.data;
}

export async function callRunDdxBenchmark(payload = {}) {
  const fn = functions.httpsCallable('callRunDdxBenchmark');
  const res = await fn(payload);
  return res.data;
}

export async function callPromoteDdxBaseline(payload = {}) {
  const fn = functions.httpsCallable('callPromoteDdxBaseline');
  const res = await fn(payload);
  return res.data;
}

export async function callRunDdxComparison(payload = {}) {
  const fn = functions.httpsCallable('callRunDdxComparison');
  const res = await fn(payload);
  return res.data;
}

export async function callRunDdxCalibrationPass(payload = {}) {
  const fn = functions.httpsCallable('callRunDdxCalibrationPass');
  const res = await fn(payload);
  return res.data;
}

export async function callExportDdxBenchmarkResults(payload = {}) {
  const fn = functions.httpsCallable('callExportDdxBenchmarkResults');
  const res = await fn(payload);
  return res.data;
}

export async function callBatchSeedDdxVignettes(payload = {}) {
  const fn = functions.httpsCallable('callBatchSeedDdxVignettes');
  const res = await fn(payload);
  return res.data;
}

/**
 * @param {{ category?: string, status?: string, ccRelevance?: string }} [filters]
 * @returns {Promise<object[]>}
 */
export async function getShiftFactors(filters = {}) {
  try {
    let query = db.collection(DDX_COLLECTIONS.SHIFT_FACTORS);
    if (filters.category) query = query.where('category', '==', String(filters.category));
    if (filters.status) query = query.where('status', '==', String(filters.status));
    if (filters.ccRelevance) query = query.where('chiefComplaintRelevance', 'array-contains', String(filters.ccRelevance));
    const snap = await query.orderBy('label', 'asc').get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[ddxService] getShiftFactors failed', e);
    return [];
  }
}

/**
 * @param {string} factorId
 * @returns {Promise<object | null>}
 */
export async function getShiftFactor(factorId) {
  if (!factorId) return null;
  try {
    const snap = await db.collection(DDX_COLLECTIONS.SHIFT_FACTORS).doc(String(factorId)).get();
    return snap.exists ? { id: snap.id, ...snap.data() } : null;
  } catch (e) {
    console.warn('[ddxService] getShiftFactor failed', e);
    return null;
  }
}

/**
 * Batch-load shift factors by document id (chunked `in` queries on documentId).
 *
 * @param {string[]} factorIds
 * @returns {Promise<object[]>}
 */
export async function getShiftFactorsByIds(factorIds) {
  const ids = [...new Set((factorIds || []).filter(Boolean).map((v) => String(v).trim()).filter(Boolean))];
  if (!ids.length) return [];
  const out = [];
  try {
    for (let i = 0; i < ids.length; i += 30) {
      const chunk = ids.slice(i, i + 30);
      // eslint-disable-next-line no-await-in-loop
      const snap = await db
        .collection(DDX_COLLECTIONS.SHIFT_FACTORS)
        .where(firebase.firestore.FieldPath.documentId(), 'in', chunk)
        .get();
      for (const doc of snap.docs) {
        out.push({ id: doc.id, ...doc.data() });
      }
    }
    return out;
  } catch (e) {
    console.warn('[ddxService] getShiftFactorsByIds failed', e);
    return [];
  }
}

/**
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function createShiftFactor(data) {
  try {
    const cleanId = String(data?.id || '').trim();
    const payload = {
      ...data,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    if (cleanId) {
      await db.collection(DDX_COLLECTIONS.SHIFT_FACTORS).doc(cleanId).set({ ...payload, id: cleanId }, { merge: true });
      return { ok: true, id: cleanId };
    }
    const ref = await db.collection(DDX_COLLECTIONS.SHIFT_FACTORS).add(payload);
    await ref.set({ id: ref.id }, { merge: true });
    return { ok: true, id: ref.id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string} factorId
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function updateShiftFactor(factorId, data) {
  try {
    const id = String(factorId || '').trim();
    if (!id) return { error: 'factorId is required' };
    await db
      .collection(DDX_COLLECTIONS.SHIFT_FACTORS)
      .doc(id)
      .set({ ...data, id, updatedAt: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true });
    return { ok: true, id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {{ specialty?: string, category?: string, cantMiss?: boolean, status?: string, edgeGenerationStatus?: string }} [filters]
 * @returns {Promise<object[]>}
 */
export async function getConditions(filters = {}) {
  try {
    let query = db.collection(DDX_COLLECTIONS.CONDITIONS);
    if (filters.specialty) query = query.where('specialty', 'array-contains', String(filters.specialty));
    if (filters.category) query = query.where('category', '==', String(filters.category));
    if (typeof filters.cantMiss === 'boolean') query = query.where('cantMiss', '==', filters.cantMiss);
    if (filters.status) query = query.where('status', '==', String(filters.status));
    if (filters.edgeGenerationStatus) query = query.where('edgeGenerationStatus', '==', String(filters.edgeGenerationStatus));
    const snap = await query.orderBy('label', 'asc').get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[ddxService] getConditions failed', e);
    return { error: e.message };
  }
}

/**
 * @param {string} conditionId
 * @returns {Promise<object | null>}
 */
export async function getCondition(conditionId) {
  if (!conditionId) return null;
  try {
    const snap = await db.collection(DDX_COLLECTIONS.CONDITIONS).doc(String(conditionId)).get();
    return snap.exists ? { id: snap.id, ...snap.data() } : null;
  } catch (e) {
    console.warn('[ddxService] getCondition failed', e);
    return null;
  }
}

/**
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function createCondition(data) {
  try {
    const cleanId = String(data?.id || '').trim();
    const payload = {
      ...data,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    if (cleanId) {
      await db.collection(DDX_COLLECTIONS.CONDITIONS).doc(cleanId).set({ ...payload, id: cleanId }, { merge: true });
      return { ok: true, id: cleanId };
    }
    const ref = await db.collection(DDX_COLLECTIONS.CONDITIONS).add(payload);
    await ref.set({ id: ref.id }, { merge: true });
    return { ok: true, id: ref.id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string} conditionId
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function updateCondition(conditionId, data) {
  try {
    const id = String(conditionId || '').trim();
    if (!id) return { error: 'conditionId is required' };
    await db
      .collection(DDX_COLLECTIONS.CONDITIONS)
      .doc(id)
      .set({ ...data, id, updatedAt: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true });
    return { ok: true, id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string} factorId
 * @returns {Promise<object[]>}
 */
export async function getShiftEdgesForFactor(factorId) {
  try {
    const snap = await db
      .collection(DDX_COLLECTIONS.SHIFT_EDGES)
      .where('factorId', '==', String(factorId || ''))
      .orderBy('shiftMagnitude', 'desc')
      .get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[ddxService] getShiftEdgesForFactor failed', e);
    return [];
  }
}

/**
 * @param {string[]} factorIds
 * @returns {Promise<Record<string, object[]>>}
 */
export async function getShiftEdgesForFactors(factorIds) {
  const normalized = [...new Set((factorIds || []).filter(Boolean).map((v) => String(v).trim()).filter(Boolean))];
  if (!normalized.length) return {};

  const edgesByFactor = {};
  try {
    for (let i = 0; i < normalized.length; i += 30) {
      const chunk = normalized.slice(i, i + 30);
      // eslint-disable-next-line no-await-in-loop
      const snap = await db
        .collection(DDX_COLLECTIONS.SHIFT_EDGES)
        .where('factorId', 'in', chunk)
        .get();

      for (const doc of snap.docs) {
        const edge = { id: doc.id, ...doc.data() };
        const fid = String(edge.factorId || '').trim();
        if (!fid) continue;
        if (!edgesByFactor[fid]) edgesByFactor[fid] = [];
        edgesByFactor[fid].push(edge);
      }
    }
  } catch (e) {
    console.warn('[ddxService] getShiftEdgesForFactors failed', e);
    return {};
  }

  return edgesByFactor;
}

/**
 * @returns {Promise<Set<string>>}
 */
export async function getKnownFactorIds() {
  const now = Date.now();
  if (_knownShiftFactorsCache.ids.length && now - _knownShiftFactorsCache.at < SHIFT_FACTORS_CACHE_MS) {
    return new Set(_knownShiftFactorsCache.ids);
  }
  try {
    const snap = await db
      .collection(DDX_COLLECTIONS.SHIFT_FACTORS)
      .where('status', '==', 'active')
      .get();
    const ids = snap.docs.map((d) => d.id);
    _knownShiftFactorsCache = { at: now, ids };
    return new Set(ids);
  } catch (e) {
    console.warn('[ddxService] getKnownFactorIds failed', e);
    return new Set();
  }
}

/**
 * @param {string[]} conditionIds
 * @returns {Promise<Record<string, string | null>>}
 */
export async function getShiftConditionDdxMap(conditionIds) {
  const ids = [...new Set((conditionIds || []).filter(Boolean).map((v) => String(v).trim()).filter(Boolean))];
  if (!ids.length) return {};
  const out = {};
  try {
    for (let i = 0; i < ids.length; i += 30) {
      const chunk = ids.slice(i, i + 30);
      // eslint-disable-next-line no-await-in-loop
      const snap = await db
        .collection(DDX_COLLECTIONS.CONDITIONS)
        .where(firebase.firestore.FieldPath.documentId(), 'in', chunk)
        .get();
      for (const doc of snap.docs) {
        const data = doc.data() || {};
        out[doc.id] = data.relatedDdxDiagnosisId ? String(data.relatedDdxDiagnosisId) : null;
      }
    }
    for (const id of ids) {
      if (!(id in out)) out[id] = null;
    }
    return out;
  } catch (e) {
    console.warn('[ddxService] getShiftConditionDdxMap failed', e);
    return {};
  }
}

/**
 * @param {string} conditionId
 * @param {{ status?: string }} [filters]
 * @returns {Promise<object[]>}
 */
export async function getShiftEdgesForCondition(conditionId, filters = {}) {
  try {
    let query = db.collection(DDX_COLLECTIONS.SHIFT_EDGES).where('conditionId', '==', String(conditionId || ''));
    if (filters.status) query = query.where('status', '==', String(filters.status));
    const snap = await query.orderBy('updatedAt', 'desc').get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[ddxService] getShiftEdgesForCondition failed', e);
    return [];
  }
}

/**
 * @returns {Promise<Record<string, { reviewed: number, pending: number }>>}
 */
export async function getEdgeCountsByCondition() {
  try {
    const snap = await db.collection(DDX_COLLECTIONS.SHIFT_EDGES).get();
    const counts = {};
    snap.docs.forEach((doc) => {
      const data = doc.data() || {};
      const conditionId = String(data.conditionId || '');
      const status = String(data.status || '');
      if (!conditionId) return;
      if (!counts[conditionId]) {
        counts[conditionId] = { reviewed: 0, pending: 0 };
      }
      if (status === 'reviewed') counts[conditionId].reviewed += 1;
      if (status === 'pending_review') counts[conditionId].pending += 1;
    });
    return counts;
  } catch (e) {
    console.warn('[ddxService] getEdgeCountsByCondition failed', e);
    return {};
  }
}

/**
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function createShiftEdge(data) {
  try {
    const cleanId = String(data?.id || '').trim();
    const payload = {
      ...data,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    if (cleanId) {
      await db.collection(DDX_COLLECTIONS.SHIFT_EDGES).doc(cleanId).set({ ...payload, id: cleanId }, { merge: true });
      return { ok: true, id: cleanId };
    }
    const ref = await db.collection(DDX_COLLECTIONS.SHIFT_EDGES).add(payload);
    await ref.set({ id: ref.id }, { merge: true });
    return { ok: true, id: ref.id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string} edgeId
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function updateShiftEdge(edgeId, data) {
  try {
    const id = String(edgeId || '').trim();
    if (!id) return { error: 'edgeId is required' };
    await db
      .collection(DDX_COLLECTIONS.SHIFT_EDGES)
      .doc(id)
      .set({ ...data, id, updatedAt: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true });
    return { ok: true, id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string} edgeId
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function deleteShiftEdge(edgeId) {
  try {
    const id = String(edgeId || '').trim();
    if (!id) return { error: 'edgeId is required' };
    await db.collection(DDX_COLLECTIONS.SHIFT_EDGES).doc(id).delete();
    return { ok: true, id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string[]} edgeIds
 * @param {object} updateData
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function bulkUpdateShiftEdges(edgeIds, updateData) {
  try {
    const ids = Array.isArray(edgeIds) ? edgeIds.filter(Boolean).map((v) => String(v)) : [];
    if (ids.length === 0) return { ok: true, id: 'none' };
    for (let i = 0; i < ids.length; i += 500) {
      const chunk = ids.slice(i, i + 500);
      const batch = db.batch();
      chunk.forEach((id) => {
        const ref = db.collection(DDX_COLLECTIONS.SHIFT_EDGES).doc(id);
        batch.set(
          ref,
          {
            ...updateData,
            id,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      });
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
    }
    return { ok: true, id: String(ids.length) };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {{ status?: string }} [filters]
 * @returns {Promise<object[]>}
 */
export async function getFactorQueue(filters = {}) {
  try {
    let query = db.collection(DDX_COLLECTIONS.FACTOR_QUEUE);
    if (filters.status) query = query.where('status', '==', String(filters.status));
    const snap = await query.orderBy('createdAt', 'desc').get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[ddxService] getFactorQueue failed', e);
    return [];
  }
}

/**
 * @param {string} queueId
 * @param {object} data
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function updateFactorQueueItem(queueId, data) {
  try {
    const id = String(queueId || '').trim();
    if (!id) return { error: 'queueId is required' };
    await db
      .collection(DDX_COLLECTIONS.FACTOR_QUEUE)
      .doc(id)
      .set({ ...data, id, updatedAt: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true });
    return { ok: true, id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}

/**
 * @param {string} queueId
 * @returns {Promise<{ ok: true, id: string } | { error: string }>}
 */
export async function createFactorFromQueue(queueId) {
  try {
    const id = String(queueId || '').trim();
    if (!id) return { error: 'queueId is required' };
    const queueSnap = await db.collection(DDX_COLLECTIONS.FACTOR_QUEUE).doc(id).get();
    if (!queueSnap.exists) return { error: 'queue item not found' };
    const queue = queueSnap.data() || {};
    const factorRef = db.collection(DDX_COLLECTIONS.SHIFT_FACTORS).doc();
    const nowTs = firebase.firestore.FieldValue.serverTimestamp();
    await factorRef.set(
      {
        id: factorRef.id,
        label: queue.suggestedLabel || '',
        description: queue.suggestedDescription || '',
        category: queue.suggestedCategory || 'history',
        subcategory: queue.suggestedSubcategory || 'general',
        factorSpecificity: queue.suggestedFactorSpecificity || 'nonspecific',
        chiefComplaintRelevance: Array.isArray(queue.suggestedChiefComplaintRelevance)
          ? queue.suggestedChiefComplaintRelevance
          : [],
        status: 'active',
        aiGenerated: true,
        createdBy: queue.reviewedBy || 'system',
        createdAt: nowTs,
        updatedAt: nowTs,
      },
      { merge: true }
    );
    await db.collection(DDX_COLLECTIONS.FACTOR_QUEUE).doc(id).set(
      {
        status: 'approved',
        mergedIntoFactorId: factorRef.id,
        reviewedAt: nowTs,
        updatedAt: nowTs,
      },
      { merge: true }
    );
    return { ok: true, id: factorRef.id };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
}
